// Centralized Firebase auth error code -> user-facing message mapping.
// Keep surface minimal; codes not listed fall back to a generic message.
// NOTE: Do not import heavy React/testing libs here; pure mapping utility.

export function mapAuthError(err: unknown, fallback: string = 'An unexpected error occurred.'): string {
  if (!err || typeof err !== 'object') return fallback;
  const code = (err as { code?: string }).code || '';
  if (code.startsWith('auth/')) {
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/invalid-email':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Invalid email or password.';
      case 'auth/missing-password':
        return 'Enter your password.';
      case 'auth/email-already-in-use':
        return 'That email is already registered.';
      case 'auth/weak-password':
        return 'Password is too weak.';
      case 'auth/expired-action-code':
        return 'This link has expired. Please request a new one.';
      case 'auth/invalid-action-code':
        return 'This link is invalid. Please request a new one.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/operation-not-allowed':
        return 'This operation is not allowed.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait and try again.';
      case 'auth/network-request-failed':
        return 'Network error. Check your connection and retry.';
      // Firebase Identity Platform Email Enumeration Protection returns this
      // instead of auth/email-already-in-use when the protection is enabled
      case 'auth/admin-restricted-operation':
        return 'That email is already registered. Please sign in instead.';
      case 'auth/captcha-check-failed':
        return 'Security check failed. Please refresh the page and try again.';
      default: {
        const msg = (err as { message?: string }).message;
        return msg || fallback;
      }
    }
  }
  const msg = (err as { message?: string }).message;
  return msg || fallback;
}

// Convenience wrappers for specific flows (semantic clarity).
export const mapLoginError = (e: unknown) => mapAuthError(e, 'Sign in failed. Please try again.');
export const mapSignupError = (e: unknown) => mapAuthError(e, 'Failed to sign up.');
export const mapResetError = (e: unknown) => mapAuthError(e, 'Failed to send reset email.');
export const mapConfirmResetError = (e: unknown) => mapAuthError(e, 'We could not reset your password. The link may be invalid or expired.');
